<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Text, Toggle, ColorWheel, Slider } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		real_id,
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_device_component_index,
	} from 'src/helpers/interface_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { format } from 'svelte-i18n';

	//services

	export let device;

	interface SettingRequestButtonPresets {
		id: number;
		config: {
			button_presets: {
				button_doublepush: {
					key: string;
					enabled: boolean;
					brightness?: number;
					rgb?: number[];
					ct?: number;
				} | null;
			};
		};
	}
	let settings = readDeviceSettings(device.id);
	const buttonPresets = settings?.[get_gen_2_component_channel_structure(device)]?.button_presets;
	let enabled =
		buttonPresets?.button_doublepush !== 'undefined' && buttonPresets?.button_doublepush !== null;

	const minBrightness = 1;
	const maxBrightness = 100;
	const mode = device?.mode;
	const supportsRgb = enabled && (mode === 'rgb' || mode === 'rgbw');

	let loading = false;

	let cacheEnabled = enabled;

	let brightness = enabled ? buttonPresets?.button_doublepush?.brightness : 100; //setting default brightness to 100% because this is correct value for web ui
	let cacheBrightness = brightness;

	const changeColor = (e) => {
		color = e.detail.color;
		rgb = joinColors(color);
	};

	const validateRgb = (_value) => {
		const max = 255;
		const min = 0;

		if (!isNaN(_value) && typeof _value !== 'number') {
			_value = Number(_value);
		}

		if (_value > max) {
			_value = max;
		}
		if (_value < min) {
			_value = min;
		}
		return +_value;
	};

	const toggleEnabled = (e) => {
		enabled = e.target.checked;
	};

	function splitColors(color) {
		return {
			r: validateRgb(color[0]),
			g: validateRgb(color[1]),
			b: validateRgb(color[2]),
		};
	}
	function joinColors(color) {
		return [validateRgb(color.r), validateRgb(color.g), validateRgb(color.b)];
	}

	let rgb = supportsRgb
		? settings?.[get_gen_2_component_channel_structure(device)]?.button_presets?.button_doublepush
				?.rgb
		: [100, 100, 100];

	let chacheRgb = [...rgb];

	let color = splitColors(rgb);

	const sendReq = async () => {
		if (
			cacheBrightness !== brightness ||
			cacheEnabled !== enabled ||
			(supportsRgb && chacheRgb !== rgb)
		) {
			try {
				loading = true;

				const config = {
					button_presets: {
						button_doublepush: {
							key: 'button_doublepush',
							enabled: enabled,
							brightness: enabled ? Number(brightness) : null,
						},
					},
				};

				if (!enabled) {
					config.button_presets.button_doublepush = null;
				}

				const params: SettingRequestButtonPresets = {
					id: get_device_component_index(device),
					config,
				};

				const res: SettingRequest = await gen2Request(
					device,
					`${get_gen_2_component_from_category(device)}.SetConfig`,
					params,
				);

				const response = await onSettingsResponse(res, device);
				if (response.isok) {
					cacheBrightness = brightness;
					cacheEnabled = enabled;
					cacheBrightness = brightness;
					if (supportsRgb) {
						chacheRgb = [...rgb];
					}
				} else {
					brightness = cacheBrightness;
					enabled = cacheEnabled;
					if (supportsRgb) {
						rgb = [...chacheRgb];
					}
					throw new Error("Button presets can't be set");
				}
				loading = false;
			} catch (error) {
				console.error(error);
			} finally {
				loading = false;
			}
		}
	};

	const a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
	};
</script>

<Text styleType="body-small" class="mb-m"
	>{$format('description_button_presets', {
		default: 'Set various properties on action. If not set it will use the current values.',
	})}</Text
>

<Toggle checked={enabled} on:click={toggleEnabled} size="m" class="mb-s" id="toggle-0cadltf"
	>{$format('action_on_btn_double_push', {
		default: 'On button double push',
	})}</Toggle
>

<Slider
	id="slider-et97l2u"
	{a11yProps}
	class="mb-s"
	value={brightness}
	on:value_change={(event) => {
		brightness = event.detail.value;
	}}
	disabled={!enabled}
	title="Brightness"
	type="simple"
	min={minBrightness}
	max={maxBrightness}
/>

<ColorWheel
	id="colorwheel-i8k7vjm"
	a11yProps={{
		saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
		colorInputBAriaLabel: $format('label_color_input_b', { default: 'Color input B' }),
		colorInputGAriaLabel: $format('label_color_input_g', { default: 'Color input G' }),
		colorInputRAriaLabel: $format('label_color_input_r', { default: 'Color input R' }),
	}}
	class="mb-xl"
	valuesType="input"
	{color}
	disabled={loading || !enabled}
	hideSaveBtn
	on:color_change={changeColor}
/>

<Button size="m" width="full" {loading} on:click={sendReq}>{$format('button_save')}</Button>
